/** Where in one upload an error came from. */
export type UploadFailureStage =
  'read' | 'sign' | 'put' | 'commit' | 'register';

/** Whether the upload host answered a cheap request after a failed PUT. Set only where one is sent. */
export type OriginReachability = 'reachable' | 'unreachable' | 'unknown';

/** What the upload learned about a failure; every field is closed or numeric. */
export type UploadFailureTrace = {
  stage?: UploadFailureStage;
  /** Signed PUTs sent for the session. Set only where the PUT is resent. */
  putAttempts?: number;
  originReachable?: OriginReachability;
};

/**
 * Side table, so a tagged error stays the exact object that was thrown: same
 * class, message and `data`, and every existing classifier reads it unchanged.
 */
const traces = new WeakMap<object, UploadFailureTrace>();

/** Records `trace` for `error`. A field already recorded is kept: first tag wins. */
export function tagUploadFailure<T>(error: T, trace: UploadFailureTrace): T {
  if (typeof error !== 'object' || error === null) return error;
  traces.set(error, { ...trace, ...traces.get(error) });
  return error;
}

export function readUploadFailureTrace(error: unknown): UploadFailureTrace {
  return typeof error === 'object' && error !== null
    ? (traces.get(error) ?? {})
    : {};
}

/**
 * Error names that may reach analytics. Anything else is reported as `Other`,
 * so a message, file name or path can never ride in on a custom class name.
 */
const REPORTABLE_ERROR_NAMES = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'AbortError',
  'TimeoutError',
  'NetworkError',
  'NotReadableError',
  'NotFoundError',
  'SecurityError',
  'QuotaExceededError',
  'InvalidStateError',
  'ConvexError',
  'BrowserUploadError',
  'BrowserPutTransportError',
  'ComposerUploadTooLargeError',
  'UploadFailedError',
]);

/** The raw `name`, for classification only. Never send this to analytics. */
export function rawErrorName(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' ? name : '';
}

export function reportableErrorName(error: unknown): string {
  if (typeof error !== 'object' || error === null) return 'NonError';
  const name = rawErrorName(error);
  return REPORTABLE_ERROR_NAMES.has(name) ? name : 'Other';
}

/** The browser refused to read the member's file (locked, changed or removed). */
export function isFileReadError(error: unknown): boolean {
  const name = rawErrorName(error);
  return (
    name === 'NotReadableError' ||
    name === 'NotFoundError' ||
    name === 'SecurityError'
  );
}

/**
 * The HTTP status an upload error carries: a structured field first (the
 * multipart part and session errors, the picker's `UploadFailedError`), else
 * the `UPLOAD_HTTP_<n>` text this client writes itself for a single PUT.
 */
export function uploadHttpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const fields = error as { httpStatus?: unknown; status?: unknown };
  const structured =
    typeof fields.httpStatus === 'number'
      ? fields.httpStatus
      : typeof fields.status === 'number'
        ? fields.status
        : undefined;
  const fromText =
    structured === undefined && error instanceof Error
      ? Number(/UPLOAD_HTTP_(\d{3})\b/.exec(error.message)?.[1])
      : undefined;
  const status = structured ?? fromText;
  return status !== undefined &&
    Number.isInteger(status) &&
    status >= 100 &&
    status <= 599
    ? status
    : undefined;
}

/**
 * The single PUT ran out of time. The client writes this text itself
 * ("Upload timed out. Please try again."); the classifier reads it only to
 * name a failure in analytics, never to decide what the upload does next.
 */
export function isUploadTimeout(error: unknown): boolean {
  return error instanceof Error && /^Upload timed out/.test(error.message);
}

/**
 * The single PUT threw instead of answering: the browser's bare XHR `error`
 * event or a failed fetch. An HTTP answer is raised as `UPLOAD_HTTP_<n>` and
 * carries a status, so a `put` failure without one, that is not a timeout and
 * not a multipart error (those carry their own structured fields), got none.
 */
export function isPutWithoutAnswer(error: unknown): boolean {
  return (
    readUploadFailureTrace(error).stage === 'put' &&
    rawErrorName(error) !== 'BrowserUploadError' &&
    uploadHttpStatus(error) === undefined &&
    !isUploadTimeout(error)
  );
}

export function browserIsOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}
